import { useState } from 'react'
import { Package, Store, ArrowRight, Search, Download, Star, Smartphone, Globe } from 'lucide-react'
import clsx from 'clsx'

// App Library - Cybertection Applications
const guardbotApps = [
  {
    id: 'guardbot',
    name: 'Guardbot',
    icon: '🤖',
    category: 'Security & Threat Detection',
    description: 'Advanced security monitoring and real-time threat detection system',
    version: '3.2.1',
    releaseDate: '2024-03-15',
    downloads: '2.1M+',
    rating: 4.9,
    developer: 'Cybertection Security Team',
    website: 'https://cybertection.com/guardbot',
    documentation: 'https://docs.cybertection.com/guardbot',
    longDescription: 'Guardbot is Cybertection\'s flagship security application, providing comprehensive protection across Windows, macOS, Linux, iOS, Android, and specialized devices. Features real-time threat analysis, behavioral detection, and cloud-based threat intelligence.',
    keyFeatures: [
      'Real-time Threat Detection',
      'Behavioral Analysis Engine',
      'Cloud-based Threat Intelligence',
      'Multi-platform Support',
      'Automatic Security Updates',
      'Zero-day Protection',
      'System Performance Optimization',
      'Incident Response Tools',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Windows',
      'macOS',
      'Linux',
      'iOS',
      'Android',
      'Fire TV',
      'Fire OS',
      'Vega OS',
      'Meta Quest',
    ],
  },
  {
    id: 'webapp-bot',
    name: 'Web App Bot',
    icon: '🌐',
    category: 'Web Application Management',
    description: 'Automated web application deployment, monitoring, and management platform',
    version: '2.1.0',
    releaseDate: '2024-02-20',
    downloads: '450K+',
    rating: 4.7,
    developer: 'Cybertection Web Platform Team',
    website: 'https://cybertection.com/webapp-bot',
    documentation: 'https://docs.cybertection.com/webapp-bot',
    longDescription: 'Web App Bot is a comprehensive web application management solution featuring automatic deployment, real-time monitoring, performance analytics, and intelligent scaling. Built for developers and DevOps teams managing cloud-based applications.',
    keyFeatures: [
      'Automated Deployment',
      'Real-time Monitoring',
      'Performance Analytics',
      'Auto-scaling',
      'Load Balancing',
      'CDN Integration',
      'API Management',
      'Security Testing',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Web Browser',
      'Windows',
      'macOS',
      'Linux',
      'iOS',
      'Android',
    ],
  },
  {
    id: 'mailbot',
    name: 'Mailbot',
    icon: '📧',
    category: 'Email & Communication',
    description: 'Intelligent email management and automation bot with AI-powered features',
    version: '1.9.2',
    releaseDate: '2024-03-01',
    downloads: '780K+',
    rating: 4.8,
    developer: 'Cybertection Communication Team',
    website: 'https://cybertection.com/mailbot',
    documentation: 'https://docs.cybertection.com/mailbot',
    longDescription: 'Mailbot revolutionizes email management with AI-powered categorization, intelligent scheduling, automated responses, and advanced security. Perfect for professionals managing high email volumes.',
    keyFeatures: [
      'AI Email Categorization',
      'Scheduled Sending',
      'Auto-reply Templates',
      'Phishing Detection',
      'Encryption Support',
      'Email Analytics',
      'Integration Suite',
      'Spam Filtering',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Web Browser',
      'Windows',
      'macOS',
      'iOS',
      'Android',
    ],
  },
  {
    id: 'guardbot-ai-live',
    name: 'Guardbot AI Live',
    icon: '🔴',
    category: 'Real-time AI Threat Detection',
    description: 'Live AI-powered threat detection and response with real-time analytics',
    version: '2.0.5',
    releaseDate: '2024-03-08',
    downloads: '320K+',
    rating: 4.9,
    developer: 'Cybertection AI Security Lab',
    website: 'https://cybertection.com/guardbot-ai-live',
    documentation: 'https://docs.cybertection.com/guardbot-ai-live',
    longDescription: 'Guardbot AI Live combines machine learning with real-time threat analysis to provide immediate threat detection and response. Powered by Cybertection\'s advanced AI models trained on millions of threat signatures.',
    keyFeatures: [
      'Real-time Threat Analysis',
      'Machine Learning Detection',
      'Instant Response Actions',
      'Behavior Profiling',
      'Anomaly Detection',
      'Live Dashboard',
      'Predictive Analytics',
      'Threat Intelligence Feed',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Web Browser',
      'Windows',
      'macOS',
      'Linux',
      'iOS',
      'Android',
    ],
  },
  {
    id: 'deepfake-scan-ai',
    name: 'Deepfake Scan AI Bot',
    icon: '🔍',
    category: 'Media Authentication & Detection',
    description: 'AI-powered deepfake detection and media authenticity verification',
    version: '1.5.3',
    releaseDate: '2024-02-10',
    downloads: '210K+',
    rating: 4.6,
    developer: 'Cybertection Media Intelligence Team',
    website: 'https://cybertection.com/deepfake-scan',
    documentation: 'https://docs.cybertection.com/deepfake-scan',
    longDescription: 'Deepfake Scan AI Bot uses advanced neural networks to detect manipulated images and videos. Essential for content verification, media authentication, and protecting against disinformation.',
    keyFeatures: [
      'Deepfake Detection',
      'Image Analysis',
      'Video Frame Analysis',
      'Manipulation Detection',
      'Confidence Scoring',
      'Batch Processing',
      'API Integration',
      'Detailed Reports',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Web Browser',
      'Windows',
      'macOS',
      'Linux',
      'iOS',
      'Android',
    ],
  },
  {
    id: 'cve-score-bot',
    name: 'CVE Score Bot',
    icon: '📊',
    category: 'Vulnerability Assessment',
    description: 'Real-time CVE tracking and vulnerability scoring system',
    version: '3.1.7',
    releaseDate: '2024-03-12',
    downloads: '560K+',
    rating: 4.8,
    developer: 'Cybertection Vulnerability Management Team',
    website: 'https://cybertection.com/cve-score-bot',
    documentation: 'https://docs.cybertection.com/cve-score-bot',
    longDescription: 'CVE Score Bot provides real-time vulnerability assessment and CVE scoring with predictive prioritization. Integrated with the latest National Vulnerability Database for comprehensive threat analysis.',
    keyFeatures: [
      'Real-time CVE Updates',
      'CVSS Scoring',
      'Vulnerability Prioritization',
      'Asset Mapping',
      'Exploit Availability Check',
      'Risk Trending',
      'Alert System',
      'Integration APIs',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Web Browser',
      'Windows',
      'macOS',
      'Linux',
    ],
  },
  {
    id: 'malware-removal-tool',
    name: 'Malware Removal Tool Bot',
    icon: '🧹',
    category: 'Malware Remediation',
    description: 'Advanced malware detection and removal tool with forensic analysis',
    version: '2.8.4',
    releaseDate: '2024-02-28',
    downloads: '890K+',
    rating: 4.7,
    developer: 'Cybertection Malware Defense Team',
    website: 'https://cybertection.com/malware-removal',
    documentation: 'https://docs.cybertection.com/malware-removal',
    longDescription: 'Malware Removal Tool Bot combines signature-based and behavioral analysis to detect and remove malware, ransomware, spyware, and other threats. Includes comprehensive forensic tools for investigation.',
    keyFeatures: [
      'Malware Detection',
      'Ransomware Removal',
      'Spyware Elimination',
      'Rootkit Detection',
      'Forensic Analysis',
      'Quarantine System',
      'Recovery Tools',
      'System Hardening',
    ],
    support: 'support@cybertection.com',
    platforms: [
      'Windows',
      'macOS',
      'Linux',
    ],
  },
]

// App Stores
const appStores = [
  {
    id: 'google-playstore',
    name: 'Google Playstore',
    icon: '📱',
    type: 'Mobile Platform',
    description: 'Official Android application distribution platform',
    apps: ['Guardbot Android', 'Web App Bot Android', 'Mailbot Android', 'Guardbot AI Live Android', 'Deepfake Scan AI Android'],
    url: 'https://play.google.com/store',
  },
  {
    id: 'microsoft-store',
    name: 'Microsoft App Store',
    icon: '🪟',
    type: 'Windows Desktop',
    description: 'Official Windows 10/11 application store',
    apps: ['Guardbot Windows', 'Web App Bot Windows', 'Mailbot Windows', 'Malware Removal Tool Bot Windows'],
    url: 'https://www.microsoft.com/store',
  },
  {
    id: 'apple-ios',
    name: 'Apple App Store iOS',
    icon: '📲',
    type: 'Apple Mobile',
    description: 'Official iOS and iPadOS application distribution',
    apps: ['Guardbot iOS', 'Web App Bot iOS', 'Mailbot iOS', 'Guardbot AI Live iOS', 'Deepfake Scan AI iOS'],
    url: 'https://www.apple.com/app-store/',
  },
  {
    id: 'apple-macos',
    name: 'Apple App Store macOS',
    icon: '🍎',
    type: 'Apple Desktop',
    description: 'Official macOS application distribution platform',
    apps: ['Guardbot macOS', 'Web App Bot macOS', 'Mailbot macOS', 'Malware Removal Tool Bot macOS'],
    url: 'https://www.apple.com/app-store/',
  },
  {
    id: 'snapcraft',
    name: 'Linux Snapcraft',
    icon: '🐧',
    type: 'Linux Distribution',
    description: 'Universal package system for Linux applications',
    apps: ['Guardbot Linux', 'Malware Removal Tool Bot Linux'],
    url: 'https://snapcraft.io',
  },
  {
    id: 'amazon-store',
    name: 'Amazon App Store',
    icon: '🛒',
    type: 'Multi-Device',
    description: 'Amazon\'s application store for Fire devices',
    apps: ['Guardbot Fire TV', 'Guardbot Fire OS'],
    url: 'https://www.amazon.com/appstore',
  },
  {
    id: 'meta-horizon',
    name: 'Meta Horizon App Store',
    icon: '🥽',
    type: 'VR/AR Platform',
    description: 'Official Meta Quest application distribution',
    apps: ['Guardbot Meta Quest'],
    url: 'https://www.meta.com/horizon/',
  },
  {
    id: 'cybertection-store',
    name: 'Cybertection App Store',
    icon: '🛡️',
    type: 'Custom Distribution',
    description: 'Direct distribution from Cybertection',
    apps: ['All Applications'],
    url: 'https://cybertection.io/store',
  },
]

export default function DeveloperStationPage() {
  const [mainView, setMainView] = useState('library')
  const [selectedApp, setSelectedApp] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const handleSelectApp = (app) => {
    setSelectedApp(app)
    setMainView('appDetail')
  }

  const handleBack = () => {
    setSelectedApp(null)
    setMainView('library')
  }

  const filteredApps = guardbotApps.filter(
    (app) =>
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.category.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <button
          onClick={() => {
            setMainView('library')
            setSearchTerm('')
          }}
          className={clsx(
            'flex flex-col items-center space-y-2 px-4 py-4 rounded-lg border-2 font-semibold transition-all',
            mainView === 'library'
              ? 'border-blue-500 bg-blue-500/20 text-blue-200'
              : 'border-blue-500/30 bg-slate-800/50 text-blue-300 hover:border-blue-500/60'
          )}
        >
          <Package size={24} />
          <span className="text-sm">Library</span>
        </button>

        <button
          onClick={() => setMainView('audit')}
          className={clsx(
            'flex flex-col items-center space-y-2 px-4 py-4 rounded-lg border-2 font-semibold transition-all',
            mainView === 'audit'
              ? 'border-purple-500 bg-purple-500/20 text-purple-200'
              : 'border-purple-500/30 bg-slate-800/50 text-purple-300 hover:border-purple-500/60'
          )}
        >
          <Store size={24} />
          <span className="text-sm">Audit</span>
        </button>
      </div>

      {/* Library View */}
      {mainView === 'library' && !selectedApp && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Developer Station</h1>
              <p className="text-blue-200 mt-1">Cybertection Application Library</p>
            </div>
          </div>

          <div className="relative mb-6">
            <Search className="absolute left-3 top-3 text-blue-300" size={20} />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredApps.map((app) => (
              <div
                key={app.id}
                onClick={() => handleSelectApp(app)}
                className="bg-gradient-to-br from-slate-800/50 to-slate-900/30 rounded-lg border border-blue-500/30 p-6 hover:border-blue-500/60 cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="text-5xl">{app.icon}</div>
                  <div className="flex items-center space-x-1 bg-yellow-500/20 px-3 py-1 rounded-full">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-yellow-400 font-bold text-xs">{app.rating}</span>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-white mb-1">{app.name}</h3>
                <p className="text-blue-400 text-xs font-semibold mb-3">{app.category}</p>
                <p className="text-xs text-blue-200 mb-4 line-clamp-2">{app.description}</p>

                <div className="text-xs text-blue-300/70">
                  v{app.version} • {app.downloads}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* App Detail View */}
      {mainView === 'appDetail' && selectedApp && (
        <>
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 px-4 py-2 text-blue-300 hover:text-blue-200 hover:bg-slate-800/50 rounded-lg transition-colors mb-6"
          >
            <ArrowRight size={20} className="rotate-180" />
            <span>Back to Library</span>
          </button>

          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-6">
                <div className="text-7xl">{selectedApp.icon}</div>
                <div>
                  <h1 className="text-4xl font-bold text-white">{selectedApp.name}</h1>
                  <p className="text-blue-300 mt-2 text-lg">{selectedApp.category}</p>
                  <p className="text-blue-300/70 mt-2">{selectedApp.developer}</p>
                </div>
              </div>
            </div>

            <p className="text-blue-200 text-lg leading-relaxed">{selectedApp.longDescription}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
              <p className="text-blue-300/70 text-sm mb-2">Version</p>
              <p className="text-2xl font-bold text-white">{selectedApp.version}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
              <p className="text-blue-300/70 text-sm mb-2">Downloads</p>
              <p className="text-2xl font-bold text-white">{selectedApp.downloads}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
              <p className="text-blue-300/70 text-sm mb-2">Rating</p>
              <p className="text-2xl font-bold text-yellow-400">{selectedApp.rating}⭐</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
              <p className="text-blue-300/70 text-sm mb-2">Platforms</p>
              <p className="text-2xl font-bold text-white">{selectedApp.platforms.length}</p>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Key Features</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {selectedApp.keyFeatures.map((feature) => (
                <div key={feature} className="flex items-center space-x-3 bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
                  <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>
                  <span className="text-blue-200 font-semibold text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Audit View */}
      {mainView === 'audit' && (
        <>
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Cybertection App Audit</h1>
            <p className="text-blue-200">Application Distribution & Store Management</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {appStores.map((store) => (
              <div key={store.id} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4 text-center hover:border-blue-500/60 transition-all">
                <p className="text-4xl mb-3">{store.icon}</p>
                <p className="font-bold text-white mb-1">{store.name}</p>
                <p className="text-xs text-blue-300">{store.apps.length} Applications</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
